import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";
import { findeAbleitungen, findeReihen } from "./erkennung";

const OHNE_ZELLEN = nichtPruefbar(
  "Die Zellinhalte",
  "1.4",
  "Sieh selbst nach, ob die Werte wie verlangt in der Spalte stehen.",
  true,
);

export const lektionAusfuellen: Lesson = {
  id: "ausfuellen",
  title: "AutoAusfüllen und Blitzvorschau",
  summary: "Reihen ziehen statt tippen – und Excel das Muster erkennen lassen.",
  durationMinutes: 10,
  learningGoals: [
    "Zahlen- und Datumsreihen mit dem Ausfüllkästchen erzeugen",
    "Reihen über Start → Ausfüllen → Reihe genau festlegen",
    "Mit der Blitzvorschau Werte aus einer anderen Spalte ableiten",
  ],
  steps: [
    {
      id: "reihen",
      title: "Reihen ausfüllen",
      explanation: [
        "Das kleine Quadrat unten rechts an der markierten Zelle heißt Ausfüllkästchen. " +
          "Zieht man es nach unten, setzt Excel ein erkanntes Muster fort: Aus 1 und 2 wird " +
          "1, 2, 3, 4 …, aus „Jan“ wird Jan, Feb, Mär …, aus einem Datum die folgenden Tage.",
        "Steht nur eine einzelne Zahl in der Markierung, kopiert Excel sie. Wer eine Reihe " +
          "will, markiert deshalb zwei Startwerte – oder hält beim Ziehen Strg gedrückt. " +
          "Die Schaltfläche „Auto-Ausfülloptionen“, die danach erscheint, wechselt zwischen " +
          "Kopieren, Reihe, nur Formate und – bei Datumswerten – Wochentagen, Monaten, Jahren.",
        "Genauer geht es über „Start“ → „Ausfüllen“ → „Reihe“: Schrittweite und Endwert " +
          "lassen sich dort eingeben, etwa „jeden Monatsersten bis Dezember“.",
      ],
      keyPoints: [
        "Zwei Startwerte markieren, dann ziehen.",
        "Auto-Ausfülloptionen: Reihe, Kopieren, Monate, Wochentage …",
        "Start → Ausfüllen → Reihe für Schrittweite und Endwert.",
      ],
      task: {
        instruction:
          "Erzeuge mit dem Ausfüllkästchen eine Reihe aus mindestens fünf Werten – etwa " +
          "Monatserste von Januar bis Mai oder die Zahlen 10, 20, 30, 40, 50.",
        hint: "Die ersten zwei Werte eintippen, beide markieren, dann das Ausfüllkästchen nach unten ziehen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) return OHNE_ZELLEN;
          const reihen = findeReihen(mappe.sheets);
          if (reihen.length === 0 && mappe.sheets.every((sheet) => sheet.cells.length === 0)) {
            return fail(KEIN_INHALT, "Tippe zwei Startwerte untereinander und zieh sie nach unten.");
          }
          const erste = reihen[0];
          return fromCriteria(
            [
              criterion(
                erste
                  ? `${erste.art} ab ${erste.sheetName}!${erste.start} mit ${erste.laenge} Werten`
                  : "Eine Reihe aus mindestens fünf gleichmäßig fortlaufenden Werten",
                reihen.length > 0,
              ),
            ],
            {
              success: "Die Reihe steht – ohne einen Wert davon zu tippen.",
              failure: "Es gibt noch keine Reihe aus mindestens fünf fortlaufenden Werten.",
              hint: "Wenn überall derselbe Wert steht, hat Excel kopiert: zwei Startwerte markieren oder Auto-Ausfülloptionen → Datenreihe ausfüllen.",
            },
          );
        },
      },
    },
    {
      id: "blitzvorschau",
      title: "Die Blitzvorschau",
      explanation: [
        "Die Blitzvorschau erkennt ein Muster aus einem Beispiel: Stehen in Spalte A " +
          "vollständige Namen und tippst du in B neben den ersten den Vornamen, schlägt " +
          "Excel beim zweiten Eintrag die übrigen Vornamen grau vor. Enter übernimmt sie. " +
          "Ausdrücklich geht es mit Strg + E oder „Daten“ → „Blitzvorschau“.",
        "Das Ergebnis sind feste Werte, keine Formeln. Kommen neue Zeilen dazu, muss die " +
          "Blitzvorschau neu laufen. Für dauerhafte Auswertungen sind deshalb die " +
          "Textfunktionen aus dem Modul „Weitere Funktionen“ besser; für eine einmalige " +
          "Bereinigung ist die Blitzvorschau unschlagbar schnell.",
        "Und: Stichproben machen. Bei Namen wie „Anna-Lena von Berg“ rät die Blitzvorschau " +
          "gern daneben.",
      ],
      keyPoints: [
        "Beispiel tippen, dann Strg + E.",
        "Ergebnis sind feste Werte – bei neuen Daten wiederholen.",
        "Stichproben prüfen, gerade bei ungewöhnlichen Einträgen.",
      ],
      task: {
        instruction:
          "Schreibe mindestens drei vollständige Namen in eine Spalte und fülle daneben mit " +
          "der Blitzvorschau die Vornamen (oder Nachnamen) aus.",
        hint: "Neben dem ersten Namen den Vornamen tippen, darunter Strg + E.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) return OHNE_ZELLEN;
          const ableitungen = findeAbleitungen(mappe.sheets);
          const erste = ableitungen[0];
          return fromCriteria(
            [
              criterion(
                erste
                  ? `Spalte ab ${erste.sheetName}!${erste.ziel} leitet ${erste.zeilen} Einträge aus ${erste.quelle} ab`
                  : "Eine Spalte mit festen Werten, die aus den Namen der Nachbarspalte stammen",
                ableitungen.length > 0,
              ),
            ],
            {
              success: "Excel hat das Muster erkannt – denk an die Stichprobe.",
              failure: "Es gibt noch keine Spalte, die aus einer anderen abgeleitet ist.",
              hint: "Die Blitzvorschau braucht die Namen direkt links daneben, ohne leere Spalte dazwischen.",
            },
          );
        },
      },
    },
  ],
};
